import * as React from "react";
import { FunctionComponent, useState, useEffect } from 'react';
import Server from "../../Server";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ClaimPackage } from "../../Models/ClaimPackage";
import { PropertyClaim } from "./PropertyClaim";
import { CSSTransition } from 'react-transition-group'; 
import { AutoClaim } from "./AutoClaim";
import { GeneralLiabilityClaim } from "./GeneralLiabilityClaim";
import { FixDate } from "../../Utils";

interface ClaimStartProps {
    isQuincy: boolean;
    policyOptions: string[];
    claimType:string;
    progressBack: () => void;
}
export const ClaimStart: FunctionComponent<ClaimStartProps> = ({isQuincy,policyOptions,claimType, progressBack}) => {
    const [width, setWidth] = useState<number>(window.innerWidth);
    const [policy, setPolicy] = useState<string>("");
	const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const [policyStart, setPolicyStart] = useState<string>("");
    const [error,setError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [claimPackage, setClaimPackage] = useState<ClaimPackage>();
    const [showPropertyClaim, setShowPropertyClaim] = useState<boolean>(false);
	const [showAutoClaim, setShowAutoClaim] = useState<boolean>(false);
	const [showGeneralLiabilityClaim, setShowGeneralLiabilityClaim] = useState<boolean>(false);
    const [showStart, setShowStart] = useState<boolean>(true);
	const [datePickerInterval, setDatePickerInterval] = useState<number>(10);
	const [lossDate, setLossDate] = useState<Date>(getDate() || undefined as any);
    const [showError, setShowError] = useState<boolean>(false);

    useEffect(() => {
		if (firstLoad) {
			let claimsDom = document.getElementById("claims-app");
			var intervalString = claimsDom!.getAttribute("data-datepickerinterval");
			var interval = parseInt(intervalString || "60");
			if (isNaN(interval))
				interval = 60;
			setDatePickerInterval(interval);
			setFirstLoad(false);
		}
	}, [firstLoad])

    function getDate()
    {
        let dateString = localStorage.getItem("autoclaim-lossdate");
        if (dateString && dateString !== "")
            return new Date(dateString);
        return undefined as any;
    }

    function getStartDate() {
		let date = new Date();
		date.setHours(0);
		date.setMinutes(0);
		date.setSeconds(0);
		return `${date.toLocaleDateString()} 12:00AM`;
	}

    function handleWindowSizeChange() {
		setWidth(window.innerWidth);
	}


	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		}
	}, []);


	const filterPassedTime = (time) => {
		const currentDate = new Date();
		const selectedDate = new Date(time);
	
		return currentDate.getTime() > selectedDate.getTime();
	  };

    function verifyPolicyAndClaim()
    {
        if (!lossDate || isNaN(lossDate.getTime()))
        {
            setShowError(true);
            return false;
        }
        else setShowError(false);

		let fixedDate = FixDate(lossDate);
        Server.sendCommand(`quincy/claim/${policyStart}${policy}/type/${claimType}/lossDate/${fixedDate.toISOString()}`,"GET",null,null,false)
			.then((resp: Response) => {
				if (resp.ok || resp.status === 400)
					return resp.json();
        })
        .then((claimPackage:ClaimPackage|string) =>{
            if (claimPackage)
			{
                if (claimPackage && typeof claimPackage !== 'string')
				{
                    claimPackage.lossDate = new Date(claimPackage.lossDate);
					setClaimPackage(claimPackage as ClaimPackage);
                    if (!claimPackage.isVerified)
                        isQuincy ? setError("We're sorry, we are unable to locate the number you entered.  Please call us at 1-800-490-0047 if you need help reporting your claim.")
                        : setError("We're sorry, we are unable to locate the number you entered.  Please call us at  1-800-542-7032 if you need help reporting your claim.");
                    else 
                    {   
                        localStorage.removeItem("autoclaim-bestcontact");
                        localStorage.removeItem("autoclaim-airbags");
                        localStorage.removeItem("autoclaim-isDriveable");
                        localStorage.removeItem("autoclaim-areinjuries");
                        localStorage.removeItem("autoclaim-arewitness");
						localStorage.removeItem("autoclaim-lossdate");
						localStorage.removeItem("generalclaim-bestcontact")
						localStorage.removeItem("generalclaim-lawsuit");
						localStorage.removeItem("generalclaim-witnesses");
                        setError("");
                        setShowStart(false);
                    }
                }
                else if (claimPackage)
                {
                    setError(claimPackage); //error string from API
                }
                setLoading(false);
            }
            else 
                setError("A general system error occurred.  Please contact the system administrator.");
			setLoading(false);
		})
    }

    function checkEnter(e: React.KeyboardEvent)
    {
        if (e.keyCode === 13) {
            setLoading(true);
            setError("");
            verifyPolicyAndClaim();
        }
    }

    function goBack()
    {
        setPolicyStart("");
        setPolicy("");
        progressBack();
    }

    function showStartSection()
    {
        setPolicyStart("");
        setPolicy("");
        setShowPropertyClaim(false);
		setShowAutoClaim(false);
		setShowGeneralLiabilityClaim(false);
        setLoading(false);
    }

    function isDisabled()
    {
        if (loading)
            return true;
        if (policy !== "" && policyStart !== "" && lossDate)
        {
            if (isNaN(lossDate.getTime()))
            {
                return true;
            }
            if (policy.length === 6)
                return false;
            return true;
        }
        return true;
    }

    function getSteps()
    {
		if (claimType.toLocaleLowerCase() === "property")
			return 2;
		else if (claimType.toLocaleLowerCase() === "auto")
			return 5;
		else
			return 5;
	}

    function setClaimScreen()
    {
		if (claimType.toLocaleLowerCase() === "property")
			setShowPropertyClaim(true);
		else if (claimType.toLocaleLowerCase() === "auto")
			setShowAutoClaim(true);
		else
			setShowGeneralLiabilityClaim(true);
    }

	//For the REPORT A CLAIM: the ternary to check if it's general liability will likely eventually need to get changed if more claim types get added with two words in them.
    let isMobile: boolean = (width <= 1024);
    return <div>
        <CSSTransition in={showStart} timeout={300}
            classNames="claim-transition"
            unmountOnExit
            onExited={() =>setClaimScreen()}>
        <div className="claim-start">
            <div>
					<h1>REPORT A CLAIM: {claimType == "generalliability" ? "GENERAL LIABILITY" : claimType.toLocaleUpperCase()} (step 1 of {getSteps()})</h1>
            </div>
            <div className="helper-header">
                {getHelpText()}
            </div>
            <p className="helper-header">To report a claim online, start by entering your policy number and the date the loss occurred.</p>
            <div className="policy-form form-horizontal">
                <div className="form-group">
                    <label className="control-label col-lg-6 col-sm-6 col-xs-6" htmlFor="policy-start">My policy begins with the letters:</label>
                    <div className="col-lg-6 col-sm-6 col-xs-6">
                        <select id="policy-start" className="form-control" aria-required value={policyStart} onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>{
                            setPolicyStart(e.target.value);
                        }}>
                            <option value="">Select...</option>
                            {policyOptions.map((option:string) =>{
                                return <option key={option} value={option}>{option}</option>
                            })}
                        </select>
                    </div>
                </div>
                <div className="form-group">
                    <label className="control-label col-lg-6 col-sm-6 col-xs-6" htmlFor="policy">Followed by these six numbers:</label>
                    <div className="col-lg-6 col-sm-6 col-xs-6">
                        <input type="text" maxLength={6} id="policy" pattern="[0-9]*" inputMode="numeric" aria-required className="form-control" value={policy} onKeyDown={checkEnter} onChange={(e: React.ChangeEvent<HTMLInputElement>) =>{
                            setPolicy(e.target.value.replace(/\D/g,''));
                        }}/>
                    </div>
                </div>
                <div className="loss-date form-group">
                    <label className="col-lg-6 col-md-6 col-sm-12 col-xs-12 control-label" htmlFor="damage-date">What date and time did the loss occur?</label>
                    <p className="sr-only">Required</p>
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                        <DatePicker
                            id="damage-date"
                            name="Date of Loss"
                            ariaRequired
                            ariaInvalid={showError && !lossDate}
                            ariaDescribedBy="dateError"
                            selected={lossDate}
                            placeholderText={getStartDate()}
                            maxDate={new Date()}
                            onChange={date => {localStorage.setItem("autoclaim-lossdate", date); setLossDate(date);}}
                            showTimeSelect
                            filterTime={filterPassedTime}
                            className="form-control"
                            dateFormat="MM/d/yy h:mm aa"
                            timeIntervals={datePickerInterval}
                            onFocus={e => e.target.blur()}
                            withPortal={isMobile} />
                        <p id="dateError" className={showError && !lossDate ? "error" : "error-hidden"}>This field is required and must be a valid date</p>
                    </div>
                </div>
            </div>
            {error !== "" ?<div className="error">
                <p>{error}</p>
            </div> : null}
            {loading ? <div className="loading">
                <p>Please wait...<i className="fa fa-spinner fa-spin"></i></p>
            </div> : null}
            <div className="progression">
                    <button className="btn btn-primary col-sm-6" type="button" onClick={() =>{goBack()}}><i className="fa fa-arrow-left" aria-hidden="true"/>&nbsp;BACK</button>
                    <button className="btn btn-primary col-sm-6" type="button" aria-disabled={isDisabled()} disabled={isDisabled()} onClick={() =>{
                        setLoading(true);
                        setError("");
                        verifyPolicyAndClaim()}}>NEXT&nbsp;<i className="fa fa-arrow-right" aria-hidden="true"/></button>
            </div>
            </div>
            </CSSTransition>
            <CSSTransition in={showPropertyClaim} timeout={300}
            classNames="claim-transition"
            unmountOnExit
            onExited={() => setShowStart(true)}>
                <PropertyClaim isQuincy={isQuincy} claimPackage={claimPackage!} progressBack={showStartSection}/>
            </CSSTransition>
            <CSSTransition in={showAutoClaim} timeout={300}
            classNames="claim-transition"
            unmountOnExit
            onExited={() => setShowStart(true)}>
                <AutoClaim isQuincy={isQuincy} claimPackage={claimPackage!} progressBack={showStartSection}/>
		</CSSTransition>
		<CSSTransition in={showGeneralLiabilityClaim} timeout={300}
			classNames="claim-transition"
			unmountOnExit
			onExited={() => setShowStart(true)}>
			<GeneralLiabilityClaim isQuincy={isQuincy} claimPackage={claimPackage!} progressBack={showStartSection} />
		</CSSTransition>
    </div>;

    function getHelpText()
    {
        if (isQuincy)
            return <div>
                <p>For <span className="emergency">EMERGENCY CLAIMS</span>, or if you do not know your policy number, please contact us directly at <strong>1-800-490-0047</strong>.</p>
            </div>;
        return <div>
            <p>If you don't know your policy number and for <span className="emergency">EMERGENCY CLAIMS</span>, please contact us directly at <strong>1-800-542-7032</strong>.
            Otherwise, if you know your policy number, please click below to report your claim online.</p>
        </div>;
    }
}
